import { api } from '../api/client'

/**
 * This browser's web push subscription: one for Thananan OS, whichever app turned it on, since every app
 * shares its origin and service worker. The API links a subscription to the signed-in user and session;
 * signing out ends it there, and `forgetPushSubscription` ends it in the browser.
 */

/** Who turned notifications on in this browser, so the next person to sign in does not inherit them */
const OWNER_KEY = 'tnn.push.owner'

export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

/** Opened as an installed app (home screen, dock) rather than in a browser tab */
export const runsInstalled = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true

/** The service worker, or undefined when it has not started within a few seconds (blocked, private mode) */
export const workerReady = () =>
  Promise.race([navigator.serviceWorker.ready, new Promise<undefined>((resolve) => setTimeout(resolve, 4000))])

export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration()
  return (await registration?.pushManager.getSubscription()) ?? null
}

const saveSubscription = (subscription: PushSubscription) => api.put('/push/subscription', subscription.toJSON())

/** Asks for permission (the browser's prompt) and subscribes this browser for `userId` */
export async function subscribePush(userId: string) {
  if ((await Notification.requestPermission()) !== 'granted') return
  const registration = await workerReady()
  if (!registration) throw new Error('No service worker')
  const { key } = await api.get<{ key: string }>('/push/key')
  // A fresh subscription, in case the old one was made with another server key
  await (await registration.pushManager.getSubscription())?.unsubscribe()
  const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  await saveSubscription(subscription)
  localStorage.setItem(OWNER_KEY, userId)
}

export async function unsubscribePush() {
  const subscription = await currentSubscription()
  localStorage.removeItem(OWNER_KEY)
  if (!subscription) return
  await api.delete('/push/subscription', { endpoint: subscription.endpoint }).catch(() => undefined)
  await subscription.unsubscribe()
}

/**
 * On every start: hand the subscription to the API again (it may have been
 * renewed, or this is a new session), unless someone else turned it on.
 */
export async function syncPushSubscription(userId: string) {
  if (!pushSupported()) return
  const subscription = await currentSubscription()
  if (!subscription) return
  if (localStorage.getItem(OWNER_KEY) !== userId) await subscription.unsubscribe()
  else await saveSubscription(subscription)
}

/** Signing out: this browser stops showing the person's notifications */
export async function forgetPushSubscription() {
  if (!pushSupported()) return
  localStorage.removeItem(OWNER_KEY)
  await (await currentSubscription())?.unsubscribe()
}

/** Asks the API to send a test notification to this browser */
export async function testPush() {
  const subscription = await currentSubscription()
  if (subscription) await api.post('/push/test', { endpoint: subscription.endpoint })
}
